/* global process */
// eslint-disable-next-line no-underscore-dangle
const __DEV__ = process.env.NODE_ENV !== 'production';

type IReducerFunc<T_STATE = any, T_ACTION = any> = (state: T_STATE, action: T_ACTION) => T_STATE;
type IWrapper = (IReducerFunc) => IReducerFunc;

const dummyWrapper: IWrapper = func => (state, action) => func(state, action);
const catchAll = new RegExp('.+');
type IHandlers = {
  [key: string]: IReducerFunc | IHandlers;
};
/*
handlers is an object with the following format:
'ACTION': reducerFunction(state, value) => nextState,

if ACTION contains *, it will be replaced by a regexp function '.+'.
if ACTION is $defaultState that state will be used initially for that key, if it's not defined on initialState.
all other ACTION starting with '$' will be ignored
 */
function createReducer<T>(
  initialState: T,
  handlers: IHandlers,
  wrapper?: IWrapper,
): IReducerFunc<T> {
  if (__DEV__) {
    if (handlers.undefined) {
      console.warn("Reducer contains an 'undefined' action type. Have you misspelled a constant?");
    }
    if (wrapper && typeof wrapper !== 'function') {
      console.warn("You passed a wrapper parameter to createReducer, but it's not a function.");
    }

    Object.keys(handlers).forEach(key => {
      if (typeof handlers[key] !== 'function' && typeof handlers[key] !== 'object') {
        console.warn(
          `You passed a value for the ${key} action that is not a function nor an object.`,
        );
      }
    });
  }

  const internalInitialState = { ...initialState };

  const internalHandlers = Object.keys(handlers)
    .filter(handlerKey => handlerKey[0] !== '$')
    .map(handlerKey => {
      const matchString = handlerKey.replace(/\*/g, '.+');
      let reducer = handlers[handlerKey];
      let re = null;

      if (typeof reducer === 'object') {
        re = catchAll;

        if (!internalInitialState[handlerKey] && reducer.$defaultState !== undefined) {
          internalInitialState[handlerKey] = reducer.$defaultState;
        }

        // eslint-disable-next-line no-use-before-define
        const subReducer = createSubReducer(
          handlerKey,
          internalInitialState[handlerKey],
          reducer,
          wrapper,
        );
        reducer = subReducer['*'];
      }

      if (typeof reducer !== 'function') {
        throw new Error(`Reducer for ${handlerKey} is not a function. got a ${typeof reducer}.`);
      }

      return {
        match: re || new RegExp(`^${matchString}$`),
        reducer,
      };
    });

  const internalWrapper = wrapper || dummyWrapper;

  return (state = initialState, action) =>
    internalHandlers.reduce(
      (currentState, { match, reducer }) =>
        match.test(action.type) ? internalWrapper(reducer)(currentState, action) : currentState,
      state,
    );
}

type ICreateSubReducer = { [key: string]: IReducerFunc };
type ICreateSubReducerOptions =
  | string
  | {
      key: string;
      prefix?: string;
      suffix?: string;
    };
/*
options object:
{ key: 'string', prefix: 'string', suffix: 'string' }

if options is an string, it assumes key is that value and everything else is ''.

prefix will be prepended to each handler action and suffix will be appended.
*/
export function createSubReducer<T = any>(
  options: ICreateSubReducerOptions,
  initialState: T,
  handlers: IHandlers,
  wrapper: IWrapper,
): ICreateSubReducer {
  let key = '';
  let prefix = '';
  let suffix = '';

  if (typeof options === 'string') {
    key = options;
  } else {
    key = options.key;
    prefix = options.prefix || '';
    suffix = options.suffix || '';
  }

  if (typeof key !== 'string' || key.length <= 0) {
    throw new Error('key option must be a non-empty string');
  }

  if (typeof prefix !== 'string') {
    throw new Error('prefix option must be ommited or a string');
  }

  if (typeof suffix !== 'string') {
    throw new Error('suffix option must be ommited or a string');
  }

  const internalHandlers = {};
  Object.keys(handlers).forEach(handlerKey => {
    const newKey = `${prefix}${handlerKey}${suffix}`;
    internalHandlers[newKey] = handlers[handlerKey];
  });

  const reducer = createReducer(initialState, internalHandlers, wrapper);

  const subReducer = (state, action) => {
    const subState = state[key] || initialState;
    const nextSubState = reducer(subState, action);

    if (nextSubState === subState) return state;

    return {
      ...state,
      [key]: nextSubState,
    };
  };

  const MatchObject = `${prefix}*${suffix}`;

  return {
    [MatchObject]: subReducer,
  };
}

type IReducer<T_STATE, T_ACTION> = (state: T_STATE, action: T_ACTION) => T_STATE;
type IActionHandler<T_RETVAL, T_STATE = any, T_ACTION = any> = (
  reducer: IReducer<T_STATE, T_ACTION>,
) => (state: T_STATE, action: T_ACTION) => T_RETVAL;

export const objectActionHandler: IActionHandler<{}> = func => (state, action) => ({
  ...state,
  ...func(state, action),
});

export const arrayActionHandler: IActionHandler<Array<any>> = func => (state, action) =>
  state.concat(func(state, action));

export default createReducer;
